import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/resources", label: "Resources" },
  { to: "/the-strong-force-daily", label: "The Strong Force Daily" },
  { to: "/shop", label: "Shop" },
  { to: "/inner-circle", label: "Inner Circle" },
  { to: "/contact", label: "Contact" },
];

const BrandMark = () => (
  <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-logo">
    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#8FAF8A] group-hover:bg-[#6E8F6A] transition-colors">
      <Leaf className="h-4.5 w-4.5 text-white" strokeWidth={2} />
    </span>
    <span className="font-serif-display font-medium text-[1.15rem] tracking-tight text-[#333333]">
      Wishing Wellness
    </span>
  </Link>
);

export default function BrandHeader({ variant = "cream" }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const baseBg = variant === "cream" ? "bg-[#F8F6F2]/85" : "bg-white/85";

  return (
    <header
      className={`sticky top-0 z-50 backdrop-blur-xl ${baseBg} transition-shadow ${scrolled ? "shadow-[0_1px_0_rgba(0,0,0,0.06)]" : ""}`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <BrandMark />
        <nav className="hidden lg:flex items-center gap-8 text-[0.92rem] text-[#4A4A4A]">
          {links.slice(1, -1).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `hover:text-[#333333] transition-colors ${isActive ? "text-[#333333] font-semibold" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:block">
          <Button
            asChild
            className="rounded-full bg-[#333333] hover:bg-black text-white px-5 h-10 font-semibold"
            data-testid="header-cta"
          >
            <Link to="/#join">Join Free</Link>
          </Button>
        </div>
        <button
          className="lg:hidden p-2 -mr-2 text-[#333333]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#EDE9E1] bg-[#F8F6F2]" data-testid="mobile-menu">
          <div className="px-6 py-5 flex flex-col gap-3 text-[#333333]">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-2 border-b border-[#EDE9E1] last:border-b-0 ${isActive ? "font-semibold" : ""}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Button
              asChild
              className="mt-3 rounded-full bg-[#333333] hover:bg-black text-white w-full"
              data-testid="mobile-header-cta"
            >
              <Link to="/#join">Join Free</Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
